import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import popupImage from "../images/popup.png";
import "./FindUnderTone.css";

const h = React.createElement;

const FindUnderTone = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isFromProfile = location.state?.fromProfile || false;

  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [questions] = useState(location.state?.questions || []);

  // Track which questions are open (expanded)
  const [openQuestions, setOpenQuestions] = useState([]);

  // Initialize all questions open
  useEffect(() => {
    if (questions && questions.length) {
      setOpenQuestions(questions.map((q) => q.id));
    }
  }, [questions]);

  const allQuestionsAnswered = questions.every((q) =>
    Object.prototype.hasOwnProperty.call(selectedAnswers, q.id)
  );

  const handleAnswerSelect = (questionId, answerId) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerId }));
    // Collapse question after answering
    setOpenQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Helper: check if quiz is complete
  const checkIfQuizComplete = async (undertoneId) => {
    const storedSkinType = sessionStorage.getItem("skin-type");
    const selectedSkinConcerns =
      JSON.parse(sessionStorage.getItem("skin-concerns")) || [];
    const storedSkinSensitivity = sessionStorage.getItem("skin-sensitivity");
    const storedSkinTone = sessionStorage.getItem("skin-tone");

    const parseAndExtractId = (storedValue) => {
      try {
        const parsedValue = JSON.parse(storedValue);
        return parsedValue?.id || null;
      } catch {
        return null;
      }
    };

    const skinTypeId = parseAndExtractId(storedSkinType);
    const skinSensitivityId = parseAndExtractId(storedSkinSensitivity);
    const skinToneId = parseAndExtractId(storedSkinTone);
    const skinConcernIds =
      selectedSkinConcerns.length > 0 ? selectedSkinConcerns : null;

    const isComplete =
      skinTypeId &&
      skinConcernIds &&
      skinConcernIds.length > 0 &&
      skinSensitivityId &&
      undertoneId &&
      skinToneId;

    // Debug
    // console.log("Quiz completion check:", {
    //   skinTypeId,
    //   skinConcernIds,
    //   skinSensitivityId,
    //   undertoneId,
    //   skinToneId,
    //   isComplete,
    // });

    return isComplete;
  };

  // Save complete quiz data
  const saveCompleteQuizData = async (undertoneId) => {
    const storedSkinType = sessionStorage.getItem("skin-type");
    const selectedSkinConcerns =
      JSON.parse(sessionStorage.getItem("skin-concerns")) || [];
    const storedSkinSensitivity = sessionStorage.getItem("skin-sensitivity");
    const storedSkinTone = sessionStorage.getItem("skin-tone");

    const parseAndExtractId = (storedValue) => {
      try {
        const parsedValue = JSON.parse(storedValue);
        return parsedValue?.id || null;
      } catch {
        return null;
      }
    };

    const skinTypeId = parseAndExtractId(storedSkinType);
    const skinSensitivityId = parseAndExtractId(storedSkinSensitivity);
    const skinToneId = parseAndExtractId(storedSkinTone);
    const skinConcernIds =
      selectedSkinConcerns.length > 0
        ? selectedSkinConcerns.map((c) => c.id)
        : null;

    const quizData = {
      skin_type: skinTypeId,
      skin_sensitive_type: skinSensitivityId,
      skin_concerns: skinConcernIds,
      skin_tone: skinToneId,
      skin_undertone: undertoneId,
    };

    try {
      const response = await fetch("https://dev-api.curu.com.au/api/profile", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(quizData),
      });

      if (!response.ok) {
        throw new Error("Failed to save the complete skin quiz data.");
      }
      return true;
    } catch (error) {
      console.error("Error saving complete quiz data:", error.message);
      return false;
    }
  };

  const submitQAFormResults = async () => {
    const selectedAnswerIds = Object.values(selectedAnswers);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "https://dev-api.curu.com.au/api/skin-undertone/verify",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ answers: selectedAnswerIds }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const { id, name, quiz_text } = result;

        setPopupMessage(
          '<div class="popup-result-skin-under-tone-QA">' +
            name +
            "</div>" +
            '<div class="popup-additional-skin-under-tone-QA">' +
            quiz_text +
            "</div>"
        );

        sessionStorage.setItem(
          "skin-underTone",
          JSON.stringify({ id, name })
        );

        if (!isFromProfile) {
          const isQuizComplete = await checkIfQuizComplete(id);
          if (isQuizComplete) {
            const saveSuccess = await saveCompleteQuizData(id);
            if (saveSuccess) {
              sessionStorage.setItem("justCompletedSkinQuiz", "true");
            }
          }
        }
      } else {
        const errorText = await response.text();
        console.error("API response error:", errorText);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }

    setShowPopup(true);
  };

  const handleBackClick = () => {
    navigate("/select-undertone", { state: { fromProfile: isFromProfile } });
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (isFromProfile) {
      navigate("/user-profile");
    } else {
      const justCompleted = sessionStorage.getItem("justCompletedSkinQuiz");
      if (justCompleted === "true") {
        navigate("/home-page");
      } else {
        navigate("/select-undertone");
      }
    }
  };

  // ---- Render helpers (no JSX) ----
  const renderAnswer = (question, answer) =>
    h(
      "li",
      { className: "answer-option-skin-under-tone-QA", key: answer.id },
      h(
        "label",
        null,
        h("input", {
          type: "radio",
          name: "question-" + question.id,
          value: answer.id,
          checked: selectedAnswers[question.id] === answer.id,
          onChange: function () {
            handleAnswerSelect(question.id, answer.id);
          },
        }),
        " ",
        answer.text
      )
    );

  const renderQuestion = (question) => {
    const isOpen = openQuestions.includes(question.id);
    const isAnswered = selectedAnswers[question.id] != null;

    return h(
      "div",
      {
        className:
          "section-skin-under-tone-QA " + (isOpen ? "" : "folded"),
        key: question.id,
      },
      // Header: toggles open/closed
      h(
        "div",
        {
          className: "question-header-skin-under-tone-QA",
          onClick: function () {
            toggleQuestion(question.id);
          },
        },
        h(
          "h6",
          { className: "question-skin-under-tone-QA" },
          question.text
        ),
        !isOpen &&
          isAnswered &&
          h(
            "div",
            { className: "custom-tickmark-container-skin-under-tone-QA" },
            h("div", { className: "custom-tickmark-skin-under-tone-QA" })
          ),
      ),

      // Answers list (only when open)
      isOpen &&
        h(
          "ul",
          { className: "answer-list-skin-under-tone-QA" },
          question.answers.map((answer) => renderAnswer(question, answer))
        )
    );
  };

  return h(
    "div",
    { className: "background-container-skin-under-tone-QA" },
    h(
      "div",
      { className: "qa-form-container-skin-under-tone-QA" },
      h("h1", null, "Find Your Skin Undertone"),
      questions.length > 0
        ? h(
            "div",
            null,
            questions.map((q) => renderQuestion(q)),
            h(
              "div",
              { className: "buttons-container-skin-under-tone-QA" },
              !isFromProfile &&
                h(
                  "button",
                  {
                    className: "back-button-skin-under-tone-QA",
                    onClick: handleBackClick,
                  },
                  "Back"
                ),
              h(
                "button",
                {
                  className:
                    "btn-complete-skin-under-tone-QA " +
                    (!allQuestionsAnswered ? "disabled" : ""),
                  onClick: allQuestionsAnswered ? submitQAFormResults : undefined,
                  disabled: !allQuestionsAnswered,
                },
                "Complete"
              )
            )
          )
        : h("p", null, "No questions available")
    ),

    showPopup &&
      h(
        "div",
        { className: "popup-overlay-skin-under-tone-QA" },
        h(
          "div",
          { className: "popup-content-skin-under-tone-QA" },
          h("img", {
            src: popupImage,
            alt: "User",
            className: "popup-image-skin-under-tone-QA",
          }),
          h("p", {
            className: "popup-message-skin-under-tone-QA",
            dangerouslySetInnerHTML: { __html: popupMessage },
          }),
          h(
            "button",
            { onClick: handleClosePopup },
            "Close"
          )
        )
      )
  );
};

export default FindUnderTone;
